import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { SUPABASE_IMAGE_BASE_URL } from '../../lib/constants';
import { ArrowLeft, AlertCircle, Check, Repeat } from 'lucide-react';
import { Toast } from '../../components/ui/Toast';
import { Helmet } from 'react-helmet';
import { FriendCodeModal } from '../../components/modals/FriendCodeModal';
import { useCollection } from '../../hooks/useCollection';
import { PageLayout } from '../../components/layout/PageLayout';
import { AdSense } from '../../components/ads/AdSense';
import { useTranslation } from '../../hooks/useTranslation';

// Fonction utilitaire pour récupérer l'URL de l'image en fonction de la langue
const getImageUrl = (imagePath: string | null | undefined, language: string) => {
  if (!imagePath) return '';
  return `${SUPABASE_IMAGE_BASE_URL}${language === 'en' ? 'en/' : ''}${imagePath}`;
};

// Fonction utilitaire pour récupérer le nom de la carte en fonction de la langue
const getCardName = (card: any, language: string) => {
  if (!card) return '';
  return language === 'en' ? (card.card_en || card.card_fr) : (card.card_fr || card.card_en);
};

// Fonction pour récupérer l'image meta pour les partages sociaux
const getMetaImage = (card: any, language: string) => {
  if (!card || !card.image) return '';
  return getImageUrl(card.image, language);
};

export const AcceptTrade = () => {
  const { tradeId } = useParams<{ tradeId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trade, setTrade] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [showFriendCodeModal, setShowFriendCodeModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const { collection } = useCollection();
  const { t, lang, getPathForLang } = useTranslation();

  useEffect(() => {
    loadTrade();
  }, [tradeId]);

  const loadTrade = async () => {
    if (!tradeId) return;

    try {
      const { data, error } = await supabase
        .from('trades')
        .select(`
          id,
          status,
          created_at,
          user_id,
          wanted_card:cards!trades_wanted_card_id_fkey(
            id,
            card_fr,
            card_en,
            image,
            set,
            rarity
          ),
          trade_cards:trade_cards!trade_cards_trade_id_fkey(
            id,
            card:cards!trade_cards_card_id_fkey(
              id,
              card_fr,
              card_en,
              image,
              set,
              rarity
            )
          ),
          creator:profiles!trades_user_id_fkey1(
            username,
            avatar_url
          )
        `)
        .eq('id', tradeId)
        .single();

      if (error) throw error;

      setTrade(data);
    } catch (error) {
      console.error('Error loading trade:', error);
      setToast({
        message: "Erreur lors du chargement de l'échange",
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTrade = async () => {
    if (!trade || !user || !selectedCard) {
      setToast({
        message: 'Veuillez sélectionner une carte à échanger',
        type: 'error'
      });
      return;
    }

    try {
      // Vérifier les informations du profil
      const { data: profile } = await supabase
        .from('profiles')
        .select('pokemon_id, game_pseudo')
        .eq('user_id', user.id)
        .single();

      if (!profile?.pokemon_id || profile.pokemon_id.trim() === '' || !profile?.game_pseudo || profile.game_pseudo.trim() === '') {
        setShowFriendCodeModal(true);
        return;
      }

      setAccepting(true);
      
      // Récupérer les informations du créateur (uniquement pour FCM token)
      const { data: creatorProfile, error: creatorError } = await supabase
        .from('profiles')
        .select('fcm_token')  // On ne récupère plus que le fcm_token
        .eq('user_id', trade.user_id)
        .single();

      if (creatorError) {
        console.error('Erreur lors de la récupération du profil créateur:', creatorError);
      }

      // Mettre à jour le statut de l'échange
      const { error } = await supabase
        .from('trades')
        .update({
          status: 'accepted',
          accepted_by: user.id,
          accepted_card_id: selectedCard,
          acceptor_pseudo: profile.game_pseudo,
          acceptor_pokemon_id: profile.pokemon_id
        })
        .eq('id', trade.id);

      if (error) throw error;

      // Créer une notification pour le créateur
      const { error: notifError } = await supabase
        .from('notifications')
        .insert({
          user_id: trade.user_id,
          title: "Votre échange a été accepté !",
          body: `${profile.game_pseudo} a accepté votre proposition d'échange.`,
          type: 'trade_accepted',
          trade_id: trade.id
        });

      if (notifError) {
        console.error('Erreur lors de la création de la notification:', notifError);
      }

      setToast({
        message: "L'échange a été accepté avec succès !",
        type: 'success'
      });

      navigate(getPathForLang(`/trades/${trade.id}`, 'en'));
    } catch (error) {
      console.error('Error accepting trade:', error);
      setToast({
        message: "Erreur lors de l'acceptation de l'échange",
        type: 'error'
      });
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!trade) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('trade_info.trade_not_found')}</h1>
          <p className="text-gray-500 mb-6">{t('trade_info.trade_not_found_message')}</p>
          <Link
            to={getPathForLang('/trades/browse', lang)}
            className="inline-flex items-center text-blue-600 hover:text-blue-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('trade_info.back_to_trades')}
          </Link>
        </div>
      </div>
    );
  }

  const metaDescription = `${trade.creator.username} propose un échange : ${getCardName(trade.wanted_card, lang)} contre une de ces ${trade.trade_cards.length} cartes.`;
  const metaImage = getMetaImage(trade.wanted_card, lang);

  return (
    <PageLayout
      breadcrumbProps={{
        items: [
          { label: t('trade_info.back_to_trades'), path: getPathForLang('/trades/browse', lang), icon: <Repeat className="w-4 h-4" /> },
          { label: t('trade_accept.accept'), path: getPathForLang(`/trades/${trade.id}/accept`, lang) }
        ]
      }}
    >
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('trade_accept.accept_trade_from')}{' '}
            <span className="text-blue-600 truncate max-w-[200px] inline-block align-bottom">{trade.creator.username}</span>
          </h1>
          <p className="text-gray-500 mt-2">
            {t('trade_info.created_on')} {new Date(trade.created_at).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </p>
        </div>

        {/* Annonce AdSense unique */}
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100 mb-6 max-w-5xl mx-auto">
          <AdSense
            adSlot="1424794127"
            className="w-full"
            style={{ minHeight: '250px', display: 'block' }}
          />
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Carte recherchée */}
            <div className="lg:w-1/3">
              <div className="sticky top-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                  <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-3 text-sm font-bold">1</span>
                  {t('trade_info.wanted_card')}
                </h2>
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100">
                  <div className="flex flex-col items-center text-center">
                    <div className="relative group">
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
                      <img
                        src={getImageUrl(trade.wanted_card.image, lang)}
                        alt={getCardName(trade.wanted_card, lang)}
                        className={`relative w-48 h-48 object-contain mb-4 transform transition duration-500 group-hover:scale-105 ${
                          user && (!collection[trade.wanted_card.id]?.quantity) ? 'grayscale' : ''
                        }`}
                      />
                      {user && (
                        <div className="absolute top-0 right-0 m-2">
                          <div className="bg-blue-600 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                            {collection[trade.wanted_card.id]?.quantity || 0}
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-xl font-semibold text-gray-900 mb-1">
                        {getCardName(trade.wanted_card, lang)}
                      </p>
                      <p className="text-sm font-medium text-blue-600">
                        {trade.wanted_card.set} • {trade.wanted_card.rarity}
                      </p>
                      {user && (
                        <p className="mt-2 text-sm">
                          {collection[trade.wanted_card.id]?.quantity || 0} {t('trade_info.specimens_in_collection')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Séparateur décoratif */}
            <div className="hidden lg:flex flex-col items-center justify-center">
              <div className="h-full w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent"></div>
            </div>

            {/* Cartes proposées */}
            <div className="lg:w-2/3">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2 flex items-center">
                  <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-3 text-sm font-bold">2</span>
                  {t('trade_info.offered_cards')}
                </h2>
                <p className="text-gray-600 ml-11">
                  {selectedCard 
                    ? t('trade_accept.card_selected_confirmation')
                    : t('trade_accept.select_card_instruction')}
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                {trade.trade_cards.map((tradeCard: any) => (
                  <div
                    key={tradeCard.id}
                    onClick={() => setSelectedCard(tradeCard.id)}
                    className={`group bg-gradient-to-br cursor-pointer ${
                      selectedCard === tradeCard.id
                        ? 'from-green-50 to-emerald-50 border-green-200 ring-2 ring-green-500 ring-offset-2'
                        : 'from-gray-50 to-white border-gray-100 hover:border-blue-200'
                    } rounded-xl p-4 border transition-all duration-300`}
                    role="button"
                    aria-pressed={selectedCard === tradeCard.id}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedCard(tradeCard.id);
                      }
                    }}
                  >
                    <div className="relative">
                      <div className={`absolute -inset-0.5 rounded-lg blur opacity-0 transition duration-1000 ${
                        selectedCard === tradeCard.id
                          ? 'bg-gradient-to-r from-green-600 to-emerald-600 opacity-25'
                          : 'bg-gradient-to-r from-blue-600 to-indigo-600 group-hover:opacity-25'
                      }`}></div>
                      <img
                        src={getImageUrl(tradeCard.card.image, lang)}
                        alt={getCardName(tradeCard.card, lang)}
                        className={`relative w-full aspect-square object-contain mb-3 transform transition duration-500 group-hover:scale-105 ${
                          user && (!collection[tradeCard.card.id]?.quantity) ? 'grayscale' : ''
                        }`}
                      />
                      {selectedCard === tradeCard.id && (
                        <div className="absolute top-2 right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center transform scale-100 animate-appear">
                          <Check className="w-5 h-5 text-white" />
                        </div>
                      )}
                      {user && (
                        <div className="absolute top-2 left-2">
                          <div className="bg-blue-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                            {collection[tradeCard.card.id]?.quantity || 0}
                          </div>
                        </div>
                      )}
                    </div>
                    <p className={`text-sm font-semibold truncate transition-colors ${
                      selectedCard === tradeCard.id
                        ? 'text-green-700'
                        : 'text-gray-900 group-hover:text-blue-600'
                    }`}>
                      {getCardName(tradeCard.card, lang)}
                    </p>
                    <p className="text-xs font-medium text-gray-500">
                      {tradeCard.card.set} • {tradeCard.card.rarity}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-col-reverse sm:flex-row justify-between items-center pt-6 border-t border-gray-200 gap-4">
            <p className="text-sm text-gray-600 w-full sm:w-auto text-center sm:text-left">
              {!selectedCard && (
                <span className="flex items-center justify-center sm:justify-start text-amber-600">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  {t('trade_accept.selection_required')}
                </span>
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <Link
                to={getPathForLang(`/trades/${trade.id}/info`, lang)}
                className="inline-flex items-center justify-center px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all duration-300 w-full sm:w-auto"
              >
                {t('trade_accept.cancel')}
              </Link>
              <button
                onClick={handleAcceptTrade}
                disabled={accepting || !selectedCard}
                className={`inline-flex items-center justify-center px-8 py-3 text-lg font-medium rounded-xl transition-all duration-300 shadow-md w-full sm:w-auto ${
                  accepting || !selectedCard
                    ? 'bg-gray-400 cursor-not-allowed opacity-50 transform-none'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 hover:shadow-lg hover:-translate-y-0.5'
                }`}
              >
                {accepting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    {t('trade_accept.accepting')}
                  </>
                ) : (
                  t('trade_info.accept_trade')
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <FriendCodeModal
        isOpen={showFriendCodeModal}
        onClose={() => setShowFriendCodeModal(false)}
        onSuccess={() => {
          setShowFriendCodeModal(false);
          handleAcceptTrade();
        }}
      />
    </PageLayout>
  );
}; 